import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Companies() {
  const { user } = useAuth();
  const isDueno = user?.rol === "dueno";
  const [companies, setCompanies] = useState([]);
  const [consecutivos, setConsecutivos] = useState({});
  const [savingConsecutivo, setSavingConsecutivo] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (c) => {
    try {
      await api.put(`/companies/${c.id}`, {
        nombre: c.nombre, pais: c.pais, moneda_default: c.moneda_default, activo: c.activo,
      });
      toast.success("Compañía actualizada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveConsecutivo = async (c) => {
    const valor = parseInt(consecutivos[c.id], 10);
    if (!valor || valor < 1) { toast.error("Ingrese un número válido"); return; }
    setSavingConsecutivo(c.id);
    try {
      const { data } = await api.put(`/companies/${c.id}/consecutivo`, { proximo_consecutivo: valor });
      toast.success(`Listo. El próximo load de ${c.nombre} será ${data.proximo_numero}`);
      setConsecutivos((prev) => ({ ...prev, [c.id]: "" }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingConsecutivo(null); }
  };

  const update = (id, field, value) =>
    setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

  return (
    <div>
      <PageHeader title="Compañías del grupo" subtitle="Trébol y Álvarez Trucking Inc (solo Dueño)" />
      <div className="grid gap-5 md:grid-cols-2">
        {companies.map((c) => (
          <Card key={c.id} data-testid={`company-card-${c.prefijo}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Building2 className="h-5 w-5 text-primary" /> {c.nombre}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Nombre</Label>
                <Input className="mt-1" value={c.nombre} onChange={(e) => update(c.id, "nombre", e.target.value)} data-testid={`company-nombre-${c.prefijo}`} />
              </div>
              <div>
                <Label className="flex items-center gap-1 text-xs text-muted-foreground">
                  Prefijo <Lock className="h-3 w-3" />
                </Label>
                <Input className="mt-1 font-mono" value={c.prefijo} disabled data-testid={`company-prefijo-${c.prefijo}`} />
                <p className="mt-1 text-xs text-muted-foreground">No se puede cambiar si ya existen loads.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">País</Label>
                  <Select value={c.pais} onValueChange={(v) => update(c.id, "pais", v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MX">México</SelectItem>
                      <SelectItem value="US">Estados Unidos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Moneda</Label>
                  <Select value={c.moneda_default} onValueChange={(v) => update(c.id, "moneda_default", v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="MXN">MXN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={() => save(c)} data-testid={`company-save-${c.prefijo}`}>Guardar cambios</Button>
              {isDueno && (
                <div className="border-t border-border pt-4">
                  <Label className="text-xs text-muted-foreground">Próximo consecutivo de numeración</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Define desde qué número sale el próximo load nuevo de {c.prefijo}. Útil para alinear con la numeración que ya usaba el cliente.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Input
                      type="number" min="1" placeholder={`ej. 296`}
                      value={consecutivos[c.id] || ""}
                      onChange={(e) => setConsecutivos((prev) => ({ ...prev, [c.id]: e.target.value }))}
                      data-testid={`company-consecutivo-input-${c.prefijo}`}
                    />
                    <Button
                      variant="outline" onClick={() => saveConsecutivo(c)}
                      disabled={savingConsecutivo === c.id}
                      data-testid={`company-consecutivo-save-${c.prefijo}`}
                    >
                      {savingConsecutivo === c.id ? "Guardando..." : "Fijar"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
